/**
 * MBR Reports Custom Hooks
 *
 * Custom hooks wrapping TanStack Query for MBR (Monthly Business Review) reports.
 * Follows the established pattern of encapsulating useQuery in custom hooks.
 *
 * Pattern: Always wrap useQuery/useMutation in custom hooks rather than
 * calling them directly in components.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { reportsApi, AvailableMonth } from "../reports-api";

/**
 * Get available months for standard MBR reports (U/Admin users)
 * @param enabled - Optional flag to conditionally enable the query (default: true)
 */
export function useAvailableMonths(enabled: boolean = true) {
  return useQuery({
    queryKey: ["mbr-months"],
    queryFn: reportsApi.getAvailableMonths,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get available months for marketing-only reports (MarketingOnly users)
 * @param enabled - Optional flag to conditionally enable the query (default: true)
 */
export function useMarketingAvailableMonths(enabled: boolean = true) {
  return useQuery({
    queryKey: ["mbr-marketing-months"],
    queryFn: reportsApi.getMarketingAvailableMonths,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get the persisted standard MBR report by month/year (U/Admin users).
 *
 * `data` is `null` when no report has been generated yet for the month —
 * components should branch on this to show a "Generate" button instead of
 * triggering generation automatically.
 */
export function useReport(year: number | undefined, month: number | undefined) {
  return useQuery({
    queryKey: ["mbr-report", year, month],
    queryFn: () => {
      if (!year || !month) {
        throw new Error("Year and month are required");
      }
      return reportsApi.getReportByMonth(year, month);
    },
    enabled: !!year && !!month,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Get the persisted marketing-only report by month/year (MarketingOnly users).
 * Returns `null` when not generated yet.
 */
export function useMarketingReport(year: number | undefined, month: number | undefined) {
  return useQuery({
    queryKey: ["marketing-report", year, month],
    queryFn: () => {
      if (!year || !month) {
        throw new Error("Year and month are required");
      }
      return reportsApi.getMarketingReport(year, month);
    },
    enabled: !!year && !!month,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Generate (or regenerate) a standard MBR report (U/Admin users).
 *
 * Same backend endpoint covers both first-time generation and
 * regeneration; the UI label differs based on whether a persisted
 * report already exists.
 */
export function useRegenerateReport(selectedMonth: AvailableMonth | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!selectedMonth) {
        throw new Error("No month selected");
      }
      return reportsApi.generateReport(selectedMonth.month, selectedMonth.year);
    },
    onSuccess: (data) => {
      if (selectedMonth) {
        queryClient.setQueryData(
          ["mbr-report", selectedMonth.year, selectedMonth.month],
          data,
        );
        // Refresh the months list so the dropdown picks up the new
        // reportId/status for this month.
        queryClient.invalidateQueries({ queryKey: ["mbr-months"] });
      }
    },
  });
}

/**
 * Generate (or regenerate) a marketing-only report (MarketingOnly users).
 */
export function useRegenerateMarketingReport(selectedMonth: AvailableMonth | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!selectedMonth) {
        throw new Error("No month selected");
      }
      return reportsApi.generateMarketingReport(selectedMonth.month, selectedMonth.year);
    },
    onSuccess: (data) => {
      if (selectedMonth) {
        queryClient.setQueryData(
          ["marketing-report", selectedMonth.year, selectedMonth.month],
          data,
        );
        queryClient.invalidateQueries({ queryKey: ["mbr-marketing-months"] });
      }
    },
  });
}
